import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Sparkles, KeyRound } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/forgot-password")({
  head: () => ({
    meta: [
      { title: "Glömt lösenord — Inkubera" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setSubmitting(false);
    if (error) {
      setError("Kunde inte skicka återställningsmejl. Kontrollera adressen.");
      return;
    }
    setSent(true);
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="px-8 py-6">
        <Link to="/" className="inline-flex items-center gap-2.5">
          <div className="size-9 rounded-xl bg-foreground flex items-center justify-center text-background">
            <Sparkles className="size-4" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display text-lg leading-none">Inkubera</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5">
              Portfolio Intelligence
            </div>
          </div>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-border bg-card p-8">
            <div className="size-10 rounded-xl bg-secondary flex items-center justify-center mb-5">
              <KeyRound className="size-4" />
            </div>
            <h1 className="font-display text-2xl">Återställ lösenord</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Vi skickar en återställningslänk till din e-post.
            </p>

            {sent ? (
              <div className="mt-6 rounded-lg bg-secondary p-4 text-sm">
                Om <span className="font-medium">{email}</span> finns i systemet
                har ett mejl skickats. Kontrollera även skräpposten.
              </div>
            ) : (
              <form onSubmit={onSubmit} className="mt-6 space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-muted-foreground">
                    E-post
                  </label>
                  <input
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 w-full h-10 px-3 rounded-lg bg-secondary border border-transparent focus:border-ring focus:outline-none text-sm"
                  />
                </div>
                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-10 rounded-lg bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-50"
                >
                  {submitting ? "Skickar…" : "Skicka återställningslänk"}
                </button>
              </form>
            )}
          </div>

          <div className="mt-4 text-center">
            <Link to="/login" className="text-xs text-muted-foreground hover:text-foreground">
              ← Tillbaka till inloggning
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
